import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useEffect, useState } from "react";

interface MetricCardProps {
  label: string;
  value: number | string;
  unit?: string;
  delta?: number; // positive = up, negative = down, undefined = no trend
  deltaLabel?: string;
  icon: LucideIcon;
  accent?: string; // Tailwind text class
  format?: "number" | "currency" | "percent" | "raw";
  decimals?: number;
}

function useCountUp(target: number, duration = 900) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (typeof target !== "number" || isNaN(target)) return;
    let raf = 0;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(target * eased);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return v;
}

export default function MetricCard({
  label, value, unit, delta, deltaLabel, icon: Icon,
  accent = "text-accent-blue", format = "number", decimals = 0,
}: MetricCardProps) {
  const numeric = typeof value === "number" ? value : NaN;
  const animated = useCountUp(numeric);
  const display = typeof value === "string"
    ? value
    : format === "currency" ? `$${animated.toFixed(decimals)}`
    : format === "percent"  ? `${animated.toFixed(decimals)}%`
    : format === "raw"      ? animated.toString()
    : animated.toLocaleString(undefined, { maximumFractionDigits: decimals });

  const TrendIcon = delta == null ? Minus : delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const trendColor = delta == null ? "text-slate-500" : delta > 0 ? "text-accent-emerald" : delta < 0 ? "text-accent-rose" : "text-slate-500";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
      className="glass glass-hover p-5 ring-glass relative overflow-hidden group hover:shadow-card-lift"
    >
      {/* Accent gradient wash on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
           style={{ background: "linear-gradient(135deg, rgba(96,165,250,0.08), rgba(167,139,250,0.04) 50%, transparent 75%)" }} />
      {/* Top edge highlight */}
      <div className="absolute inset-x-0 top-0 h-px opacity-60 pointer-events-none"
           style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)" }} />

      <div className="relative flex items-start justify-between">
        <div>
          <div className="eyebrow">{label}</div>
          <div className="mt-2 text-3xl font-bold font-sans tracking-tight gradient-text">
            {display}{unit && <span className="text-slate-500 text-lg ml-1 font-medium">{unit}</span>}
          </div>
          {(delta != null || deltaLabel) && (
            <div className={`mt-2 flex items-center gap-1.5 text-xs ${trendColor}`}>
              <TrendIcon size={12} strokeWidth={2.5} />
              <span className="font-mono">
                {delta != null && `${delta > 0 ? "+" : ""}${delta}%`}
                {deltaLabel && <span className="text-slate-500 ml-1.5">{deltaLabel}</span>}
              </span>
            </div>
          )}
        </div>
        <div className={`w-10 h-10 rounded-lg bg-white/[0.05] border border-white/[0.10] flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] group-hover:border-white/[0.16] group-hover:bg-white/[0.07] transition-all ${accent}`}>
          <Icon size={18} strokeWidth={1.75} />
        </div>
      </div>
    </motion.div>
  );
}
